import moment from 'moment';
import { Observable } from 'rxjs';
import { RequestRoot, UTCDate } from 'types';
import { ValidationSeveritySetting } from 'validation';
import { DocumentField, MaskedDate, NullableMaskedDate, ImageBlob, Enum, ArrayColumnSetter } from './features-decl';
import { AppContext } from '../control/context';
import { LuxonDateParser } from '../parser/luxon-date-parser';
import { RecognitionMode } from '../parser/date-parser';
import { IFormattingOptions } from './features';

export enum DatePickerType {
  None = 0,
  YMD = 1,
  YM = 2,
  Y = 3,
}

export function getPath(pathLambda: (_: any) => any): string {
  return /(\.\w+)+/.exec(pathLambda.toString())![0]!.substring(1);
}

export class VisibleItem {
  rootPrototype: any;
  nodePrototype: any;
  targetProperty: string;
  targetVisibilityLambda: (_: any) => boolean;
}

export class MaskedDateItem<T> {
  static formatMaskedDateMonth(index: number, locale: string, short: boolean): string {
    const localeData = moment.localeData(locale);
    let result: string;
    if (short) {
      result = localeData.monthsShort()[index].toUpperCase();
      if (result.endsWith('.')) {
        result = result.substr(0, result.length - 1);
      }
      if (locale === 'ru-RU') {
        result = result.substr(0, 3);
      }
    } else {
      result = (localeData as any)._months.format[index].toUpperCase();
    }
    return result;
  }

  rootPrototype: T;
  targetProperty: string;
  dmy: boolean;
  my: boolean;
  y: boolean;
  maxDate: (() => UTCDate) | null;

  normalize(value: string, locale: string): string {
    const modes: RecognitionMode[] = [];
    if (this.dmy) {
      modes.push(RecognitionMode.DMY);
    }
    if (this.my) {
      modes.push(RecognitionMode.MY);
    }
    if (this.y) {
      modes.push(RecognitionMode.Y);
    }

    let parser = new LuxonDateParser();
    let result = parser.parse(value, locale.substr(0, 2), modes);

    if (result.isSucceded()) {
      let year = result.year > 0 ? result.year.toString().padStart(4, '0') : '0000';
      let month = result.month > 0 ? result.month.toString().padStart(2, '0') : '00';
      let day = result.day > 0 ? result.day.toString().padStart(2, '0') : '00';
      return year + '-' + month + '-' + day;
    }
    return '';
  }
}

export class EnumItem<T> {
  rootPrototype: T;
  targetProperty: string;
  sourceProperty: string;
  listGenerator: (root: any) => Enum[];
  defaultGenerator: (root: any) => Enum | null;
}

export class StringItem<T> {
  rootPrototype: T;
  targetProperty: string;
  multiline: boolean;
  uppercase: boolean;
  readonly: boolean;
}

export class Calculator<T> {
  rootPrototype: T;
  targetProperty: string;
  pathPrefix: string;
  paths: string[];
  calculator: (root: any, pathPrefix: string, paths: string[]) => any;
  ignoreNulls: boolean = false;

  private index(path: string, indexedPathSteps: string[]): string[] {
    const pathSteps = path.split('.');
    if (pathSteps.length !== indexedPathSteps.length) {
      return pathSteps;
    }
    for (let i = 0; i < pathSteps.length; ++i) {
      if (pathSteps[i] === 'template' && /^\d+$/.test(indexedPathSteps[i])) {
        pathSteps[i] = indexedPathSteps[i];
      }
    }
    return pathSteps;
  }

  calculate(model: any, indexedPathSteps: string[]) {
    const sourcePaths = this.paths.map((path) => this.index(path, indexedPathSteps).join('.'));
    const targetPath = this.index(this.targetProperty, indexedPathSteps);
    const it = targetPath.slice(0, -1).reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), model);
    if (it !== undefined) {
      const calculatedValue = this.calculator(model, this.pathPrefix, sourcePaths);
      if (!!calculatedValue || !this.ignoreNulls) {
        it[targetPath.slice(-1)[0]] = calculatedValue;
      }
    }
  }
}

export class TreeItem {
  prot: any;
  prop: string;
}

export class Column<T> {
  rootPrototype: T;
  cortegeName: string;
  viewPixelSize: number;
  targetProperty: string;
  searchable: boolean;
  editPercentSize: number;
}

export class ArrayInfo {
  rootPrototype: any;
  compare: ((recordA: any, recordB: any) => any) | undefined;
  onEditClosed: ((lastRecord: any, arr: any[]) => any) | undefined;
  dateRanges: Array<{ propFrom: string; propTo: string }>;
}

export class MobileArrayInfo {
  rootPrototype: any;
  firstLineFormatter?: (record: any, options: IFormattingOptions) => string;
  secondLineFormatter?: (record: any, options: IFormattingOptions) => string;
}

export class ArrayColumnInfo {
  rootPrototype: any;
  prop: string;
  viewFractionSize: number;
  minTableWidthVisible: number;
  setters: ArrayColumnSetter<any>[];
  /** Поддерживает компактное отображение */
  compact: boolean;
}

export class ArrayColumnEditInfo {
  rootPrototype: any;
  minWidth: number;
  editPixelSizes: number[];
}

export class DocumentFieldInfo {
  rootPrototype: any;
  field: DocumentField;
  targetProperty: string;
  targetPropertyIsConstant: boolean;
}

export class ValidationSpecification {
  rootPrototype: any;
  additionalRules: any;
  specification: any;
  warnings: ValidationSeveritySetting[];
  notifications: ValidationSeveritySetting[];
}

export class FeaturesImpl {
  static deferredMetadata: any[] = [];

  static makeString(model: RequestRoot, path: string, appContext: AppContext, miss?: string): string {
    const value = path.split('.').reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), model?.features);
    if (value instanceof Object) {
      if (value instanceof ImageBlob) {
        return model.readBlobURL(path);
      }
      if (value instanceof MaskedDate || value instanceof NullableMaskedDate) {
        return (
          FeaturesImpl.formatMaskedDate(null, value.value, appContext.getLocale()) ||
          (value.value === null && miss ? appContext.getI18NString(miss) : '')
        );
      }
      if (Object.getPrototypeOf(value).constructor.unwrap) {
        return Object.getPrototypeOf(value).constructor.unwrap(value) || '';
      }
      return String(value);
    }
    if (value === false) {
      return appContext.getI18NString('NO');
    }
    if (value === true) {
      return appContext.getI18NString('YES');
    }
    if (typeof value === 'number' || typeof value === 'string') {
      return String(value);
    }
    return '';
  }

  static makeDatePartString(model: RequestRoot, part: 'D' | 'M' | 'Y', path: string, appContext: AppContext): string {
    const value = path.split('.').reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), model?.features);
    if (value instanceof MaskedDate || value instanceof NullableMaskedDate) {
      return FeaturesImpl.formatMaskedDate(part, value.value, appContext.getLocale(), null) || '';
    }
    return '';
  }

  private static findSubPrototype(prototype: any, property: string): any {
    const items = FeaturesImpl.tree.get(prototype);
    if (items) {
      for (const x of items) {
        if (x.prop === property) {
          return x.prot;
        }
      }
    }
    throw Error('Property ' + property + ' not found in ' + prototype.constructor.name);
  }

  private static fixPropertyKey(k: string): string {
    const result = k.replace(/\.\d+\./g, '.template.');
    return result;
  }

  private static arrayIndexes(k: string): number[] {
    return (k.match(/\.\d+\./g) || []).map((m) => Number(m.substr(1, m.length - 2)));
  }

  static visibleItems: VisibleItem[] = [];

  static showField(
    currentNodePrototype: any,
    currentNodeObj: any,
    currentPath: string[],
    baseNodePrototype: any,
    baseNodeObj: any,
    targetVisibilityLambda: (_: any) => boolean,
    targetTemplate: any,
  ) {
    if (currentNodeObj === undefined) {
      return;
    }
    if (baseNodePrototype === currentNodePrototype) {
      baseNodeObj = currentNodeObj;
    }
    if (currentPath.length === 1) {
      const visible = targetVisibilityLambda(baseNodeObj);
      if (visible && currentNodeObj[currentPath[0]] === undefined) {
        currentNodeObj[currentPath[0]] = targetTemplate;
      }
      if (!visible && currentNodeObj[currentPath[0]] !== undefined) {
        currentNodeObj[currentPath[0]] = undefined;
      }
    } else if (currentNodePrototype.constructor.name === 'FeatureArray') {
      for (let i = 0; i < currentNodeObj.length; ++i) {
        FeaturesImpl.showField(
          Object.getPrototypeOf(currentNodeObj.template),
          currentNodeObj[i],
          currentPath.slice(1),
          baseNodePrototype,
          baseNodeObj,
          targetVisibilityLambda,
          targetTemplate,
        );
      }
    } else {
      const subPrototype = FeaturesImpl.findSubPrototype(currentNodePrototype, currentPath[0]);
      FeaturesImpl.showField(
        subPrototype,
        currentNodeObj[currentPath[0]],
        currentPath.slice(1),
        baseNodePrototype,
        baseNodeObj,
        targetVisibilityLambda,
        targetTemplate,
      );
    }
  }

  static maskedDateItems: MaskedDateItem<any>[] = [];

  static queryMaskedDate(_changedPropertyKey: string, model: any): { type: DatePickerType; maxDate: UTCDate | null } {
    const result: { type: DatePickerType; maxDate: UTCDate | null } = { type: DatePickerType.None, maxDate: null };
    const changedPropertyKey = FeaturesImpl.fixPropertyKey(_changedPropertyKey);
    FeaturesImpl.maskedDateItems
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.targetProperty === changedPropertyKey)
      .forEach((it) => {
        result.type = it.dmy ? DatePickerType.YMD : it.my ? DatePickerType.YM : it.y ? DatePickerType.Y : DatePickerType.None;
        result.maxDate = it.maxDate ? it.maxDate() : null;
      });
    return result;
  }

  static parseMaskedDate(_changedPropertyKey: string, model: any, val: string | null, locale: string): string | null {
    if (val === null || val === '') {
      return val;
    }
    if (!model) {
      const x = new MaskedDateItem<any>();
      x.y = true;
      x.dmy = true;
      return x.normalize(val, locale);
    }
    let result: string = '';
    const changedPropertyKey = FeaturesImpl.fixPropertyKey(_changedPropertyKey);
    FeaturesImpl.maskedDateItems
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.targetProperty === changedPropertyKey)
      .forEach((it) => {
        result = it.normalize(val, locale);
      });
    return result;
  }

  static formatMaskedDate(
    part: 'D' | 'M' | 'Y' | null,
    val: string | null,
    locale: string,
    monthFormat: 'letters' | null = null,
  ): string | null {
    if (val === null || val === '') {
      return val;
    }
    const x = val.split('-');
    let result = '';
    if (x[2] !== '00' && (part === null || part === 'D')) {
      result += x[2];
    }
    if (x[1] !== '00' && (part === null || part === 'M')) {
      if (result.length > 0) {
        result += '.';
      }
      result += monthFormat === 'letters' ? MaskedDateItem.formatMaskedDateMonth(+x[1] - 1, locale, part === null) : x[1];
    }
    if (x[0] !== '0000' && (part === null || part === 'Y')) {
      if (result.length > 0) {
        result += '.';
      }
      result += x[0];
    }
    return result;
  }

  static enumItems: EnumItem<any>[] = [];

  static queryEnumOptions(_changedPropertyKey: string, model: any): { listGenerator?: (root: any) => Enum[] } {
    let result: { listGenerator?: (root: any) => Enum[] } = {};
    const changedPropertyKey = FeaturesImpl.fixPropertyKey(_changedPropertyKey);
    FeaturesImpl.enumItems
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.targetProperty === changedPropertyKey)
      .forEach((it) => {
        result.listGenerator = it.listGenerator;
      });
    return result;
  }

  static stringItems: StringItem<any>[] = [];

  static queryStringOptions(_changedPropertyKey: string, model: any): { multiline: boolean; uppercase: boolean; readonly: boolean } {
    let result = { multiline: false, uppercase: true, readonly: false };
    const changedPropertyKey = FeaturesImpl.fixPropertyKey(_changedPropertyKey);
    FeaturesImpl.stringItems
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.targetProperty === changedPropertyKey)
      .forEach((it) => {
        result.multiline = it.multiline;
        result.uppercase = it.uppercase;
        result.readonly = it.readonly;
      });
    return result;
  }

  static calculators: Calculator<any>[] = [];

  static keyboardLanguages: { [modelPropertyPath: string]: 'en' | 'ru' | null } = {};

  static updateDefaultValues(_changedPropertyKey: string, model: any) {
    const changedPropertyKey = FeaturesImpl.fixPropertyKey(_changedPropertyKey);
    FeaturesImpl.calculators
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.paths.includes(changedPropertyKey))
      .forEach((it) => {
        it.calculate(model, _changedPropertyKey.split('.'));
      });
  }

  static dicts: { [modelPropertyPath: string]: (a: any, prefix: string, arrayIndexes: number[]) => Observable<string[]> } = {};

  static getSuggestedOptions(_modelPropertyPath: string): ((a: any, prefix: string) => Observable<string[]>) | undefined {
    const modelPropertyPath = FeaturesImpl.fixPropertyKey(_modelPropertyPath);
    const arrayIndexes = FeaturesImpl.arrayIndexes(_modelPropertyPath);
    const impl = FeaturesImpl.dicts[modelPropertyPath];
    if (!impl) {
      return undefined;
    }
    return (a: any, prefix: string) => {
      return impl(a, prefix, arrayIndexes);
    };
  }

  static tree = new Map<any, TreeItem[]>();

  private static doReadTree(parentSrc: any, parentDst: any, prop: string) {
    const src = parentSrc[prop];
    if (src === undefined) {
      parentDst[prop] = undefined;
    } else {
      const dst = parentDst[prop];
      const p = FeaturesImpl.tree.get(Object.getPrototypeOf(dst));
      if (p === undefined) {
        if (dst instanceof Array) {
          dst.length = 0;
          for (let i = 0; i < src.length; ++i) {
            const elem = new (dst as any).template.constructor();
            FeaturesImpl.readTree(src[i], elem);
            dst.push(elem);
          }
        } else if (dst instanceof Object) {
          parentDst[prop] = Object.getPrototypeOf(dst).constructor.deserialize(dst, src);
        } else {
          parentDst[prop] = src;
        }
      } else {
        p.forEach((q) => {
          FeaturesImpl.doReadTree(src, dst, q.prop);
        });
      }
    }
  }

  static readTree(src: any, dst: any) {
    FeaturesImpl.doReadTree({ val: src }, { val: dst }, 'val');
  }

  static findPrototype(prototype: any, model: any, path: string[]): any {
    if (path.length === 0) {
      return prototype;
    }
    let subPrototype: any;
    let subTemplate: any;
    if (prototype.constructor.name === 'FeatureArray') {
      subPrototype = Object.getPrototypeOf(model.template);
      subTemplate = model.template;
    } else {
      subPrototype = FeaturesImpl.findSubPrototype(prototype, path[0]);
      subTemplate = model[path[0]];
    }
    return FeaturesImpl.findPrototype(subPrototype, subTemplate, path.slice(1));
  }

  static makeTemplate(prototype: any, template: any, path: string[]): any {
    if (path.length === 0) {
      return template;
    }
    let subPrototype: any;
    if (prototype.constructor.name === 'FeatureArray') {
      subPrototype = Object.getPrototypeOf(template.template);
    } else {
      subPrototype = FeaturesImpl.findSubPrototype(prototype, path[0]);
    }
    let subTemplate: any;
    if (path.length > 1) {
      subTemplate = template[path[0]];
    } else if (prototype.constructor.name === 'FeatureArray') {
      subTemplate = new template.template.constructor();
    } else if (subPrototype.constructor.name === 'FeatureArray') {
      subTemplate = new subPrototype.constructor(template[path[0]].template.constructor, []);
    } else if (['Boolean', 'Number', 'String'].includes(subPrototype.constructor.name)) {
      subTemplate = template[path[0]];
    } else {
      subTemplate = new subPrototype.constructor();
      if (subTemplate instanceof Enum) {
        subTemplate = template[path[0]];
      }
    }
    return FeaturesImpl.makeTemplate(subPrototype, subTemplate, path.slice(1));
  }

  static columns: Column<any>[] = [];

  static getColumnCorteges(model: any): Set<string> {
    const names = new Set<string>();
    FeaturesImpl.columns
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model))
      .forEach((it) => {
        names.add(it.cortegeName);
      });
    return names;
  }

  static getColumns(model: any, cortegeName: string): Array<string> {
    const columns = new Array<string>();
    FeaturesImpl.columns
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.cortegeName === cortegeName)
      .forEach((it) => {
        columns.push(it.targetProperty);
      });
    return columns;
  }

  static getColumnViewFlexStyle(model: any, cortegeName: string, column: string): string {
    let result: string = '';
    FeaturesImpl.columns
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.cortegeName === cortegeName && it.targetProperty === column)
      .forEach((it) => {
        result = it.viewPixelSize + ' 1 ' + it.viewPixelSize + 'px';
      });
    return result;
  }

  static getColumnViewFlexWidth(model: any, cortegeName: string, extra: number): string {
    let sum = 0;
    FeaturesImpl.columns
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.cortegeName === cortegeName)
      .forEach((it) => {
        sum += it.viewPixelSize;
      });
    const result = extra + sum + 'px';
    return result;
  }

  static getColumnEditFlexStyle(model: any, column: string): string {
    let result: string = '';
    FeaturesImpl.columns
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.targetProperty === column)
      .forEach((it) => {
        result = it.editPercentSize + ' 1 ' + it.editPercentSize + '%';
      });
    return result;
  }

  static arrayInfos: ArrayInfo[] = [];
  static mobileArrayInfos: MobileArrayInfo[] = [];

  static getArrayCompareFunction(model: any): ((a: any, b: any) => any) | undefined {
    const info = FeaturesImpl.arrayInfos.find((it) => it.rootPrototype === Object.getPrototypeOf(model));
    return info?.compare;
  }

  static getArrayEditClosedCallback(model: any): ((r: any, arr: any[]) => any) | undefined {
    const info = FeaturesImpl.arrayInfos.find((it) => it.rootPrototype === Object.getPrototypeOf(model));
    return info?.onEditClosed;
  }

  static getArrayDateRanges(model: any): Array<{ propFrom: string; propTo: string }> | undefined {
    const info = FeaturesImpl.arrayInfos.find((it) => it.rootPrototype === Object.getPrototypeOf(model));
    return info?.dateRanges;
  }

  static arrayColumnInfos: ArrayColumnInfo[] = [];

  static getArrayViewFlexStyle(model: any, propertyName: string): string {
    let result: string = '';
    FeaturesImpl.arrayColumnInfos.filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.prop === propertyName);
    return result;
  }

  static getArrayViewTablePropVisible(model: any, propertyName: string): boolean {
    let result = true;
    FeaturesImpl.arrayColumnInfos.filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.prop === propertyName);
    return result;
  }

  static getArrayEditSetters(model: any, propertyName: string): ArrayColumnSetter<any>[] {
    let result: ArrayColumnSetter<any>[] = [];
    FeaturesImpl.arrayColumnInfos
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model) && it.prop === propertyName)
      .forEach((it) => (result = it.setters));
    return result;
  }

  static formatMobileArrayFirstLine(model: any, appContext: AppContext): string {
    let info = FeaturesImpl.mobileArrayInfos.find((it) => it.rootPrototype === Object.getPrototypeOf(model));
    return !!info && !!info.firstLineFormatter ? info.firstLineFormatter(model, FeaturesImpl.createFormattingOptions(appContext)) : '';
  }

  static formatMobileArraySecondLine(model: any, appContext: AppContext): string {
    let info = FeaturesImpl.mobileArrayInfos.find((it) => it.rootPrototype === Object.getPrototypeOf(model));
    return !!info && !!info.secondLineFormatter ? info.secondLineFormatter(model, FeaturesImpl.createFormattingOptions(appContext)) : '';
  }

  static createFormattingOptions(appContext: AppContext): IFormattingOptions {
    return {
      dateFormatter: (date: MaskedDate | NullableMaskedDate, miss?: string) => {
        return (
          FeaturesImpl.formatMaskedDate(null, date.value, appContext.getLocale()) ||
          (date.value === null && miss ? appContext.getI18NString(miss) : '')
        );
      },
    };
  }

  static arrayColumnEditInfos: ArrayColumnEditInfo[] = [];

  static getArrayEditGridStyle(model: any, windowWidth: number): string {
    const infos = FeaturesImpl.arrayColumnEditInfos
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model))
      .sort((a, b) => b.minWidth - a.minWidth);
    let info: ArrayColumnEditInfo | null = null;
    for (let i = 0; i < infos.length; i++) {
      if (windowWidth >= infos[i].minWidth) {
        info = infos[i];
        break;
      }
    }
    return info !== null ? info.editPixelSizes.map((x) => x + 'px').join(' ') : '';
  }

  static documentFields: DocumentFieldInfo[] = [];

  static formatDocumentField(model: RequestRoot, field: DocumentField, appContext: AppContext): string {
    let result: string = '';
    FeaturesImpl.documentFields
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(model.features) && it.field === field)
      .forEach(
        (it) => (result = it.targetPropertyIsConstant ? it.targetProperty : FeaturesImpl.makeString(model, it.targetProperty, appContext)),
      );
    return result;
  }

  static validationSpecifications: ValidationSpecification[] = [];

  static getValidationSpecification(model: any): ValidationSpecification {
    const result = FeaturesImpl.validationSpecifications.filter((it) => it.rootPrototype === Object.getPrototypeOf(model))[0];
    return result;
  }
}
